/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: the workouts of one day (or week, or the whole list), each with its actions.
 */
import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from "@lemonadetraining/web-ui";
import { IconDotsVertical, IconPlus } from "@tabler/icons-react";
import WorkoutPartView from "../workout/WorkoutPartView";
import ViewAthletesScoresWidget from "../../Workout/ViewAthletesScoresWidget";
import WorkoutScore from "../../Workout/WorkoutScore";
import type { PlannedWorkout } from "../../../hooks/useProgramWorkouts";

export interface WorkoutSlotProps {
  heading: string;
  subheading?: string;
  workouts: PlannedWorkout[];
  status: "loading" | "ready" | "error";
  canManage: boolean;
  emptyText: string;
  addLabel?: string;
  onAdd: () => void;
  onEdit: (workout: PlannedWorkout) => void;
  onDuplicate: (workout: PlannedWorkout) => void;
  onRemove: (workout: PlannedWorkout) => void;
  onRetry: () => void;
  /** Private notes by workout id, with the label that says who sees them. */
  notes?: { label: string; byId: Record<string, string>; failed?: boolean; onRetry?: () => void };
}

type ResultsView = { workout: PlannedWorkout; mode: "see" | "record" } | null;

export function WorkoutSlot({
  heading,
  subheading,
  workouts,
  status,
  canManage,
  emptyText,
  addLabel = "Add workout",
  onAdd,
  onEdit,
  onDuplicate,
  onRemove,
  onRetry,
  notes,
}: WorkoutSlotProps) {
  const [results, setResults] = useState<ResultsView>(null);
  const titleOf = (w: PlannedWorkout) => w.workoutData.title || "Untitled workout";

  return (
    <section className="flex min-w-0 flex-col gap-3" aria-label={heading}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-base font-semibold">{heading}</h2>
        {subheading && <span className="text-sm text-muted-foreground">{subheading}</span>}
      </div>
      {status === "ready" && notes?.failed && (
        <p
          className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground"
          role="status"
        >
          We couldn&apos;t load your private notes.
          <Button variant="link" className="h-auto min-h-9 p-0 text-sm" onClick={notes.onRetry}>
            Try again
          </Button>
        </p>
      )}
      {status === "loading" ? (
        <div className="flex flex-col gap-3" data-testid="slot-loading">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : status === "error" ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border p-4" role="alert">
          <p className="text-sm">We couldn&apos;t load the workouts. Check your connection.</p>
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          {workouts.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="slot-empty">
              {emptyText}
            </p>
          )}
          <ul className="flex w-full max-w-[38rem] flex-col gap-3">
            {workouts.map((workout) => (
              <li
                key={workout.id}
                className="rounded-xl border bg-card p-4"
                data-testid={`workout-${workout.id}`}
              >
                <WorkoutPartView
                  workout={workout}
                  actions={
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Actions for ${titleOf(workout)}`}
                          className="relative after:absolute after:-inset-2 after:content-['']"
                          data-testid={`workout-menu-${workout.id}`}
                        >
                          <IconDotsVertical className="size-4" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setResults({ workout, mode: "see" })}>
                          See results and comments
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setResults({ workout, mode: "record" })}>
                          Record a result
                        </DropdownMenuItem>
                        {canManage && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => onEdit(workout)}>
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onDuplicate(workout)}>
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => onRemove(workout)}
                            >
                              Remove…
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  }
                />
                {notes?.byId[workout.id] && (
                  <div
                    className="mt-3 rounded-md border-l-2 border-info bg-muted/40 px-3 py-2"
                    data-testid={`workout-note-${workout.id}`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-info">
                      {notes.label}
                    </p>
                    <p className="whitespace-pre-line break-words text-sm">
                      {notes.byId[workout.id]}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {canManage && (
            <button
              type="button"
              onClick={onAdd}
              className="flex min-h-11 w-full max-w-[38rem] items-center justify-center gap-2 rounded-xl border border-dashed text-sm text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="slot-add"
            >
              <IconPlus className="size-4" aria-hidden="true" />
              {addLabel}
            </button>
          )}
        </>
      )}
      <Dialog open={results !== null} onOpenChange={(open) => !open && setResults(null)}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto"
          data-testid="workout-results-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {results?.mode === "record" ? "Record a result" : "Results and comments"}
            </DialogTitle>
            <DialogDescription>{results ? titleOf(results.workout) : ""}</DialogDescription>
          </DialogHeader>
          {results?.mode === "see" && (
            <ViewAthletesScoresWidget workoutCardUID={results.workout.id} />
          )}
          {results?.mode === "record" && (
            <WorkoutScore
              workoutUID={results.workout.id}
              onSubmitFinished={(saved?: boolean) => saved === true && setResults(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
